import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMergedRef } from "@ajayvikas/react-common";
import classNames from "classnames";
import React from "react";
import { PopOver } from "../poppers/PopOver";
import { Caret } from "../icons/Caret";
import { Clear } from "../icons/Clear";
import { AutoExpandInput } from "../react-input/AutoExpandInput";
import { ADD_NEW, NO_RECORD, NULL_VALUE, SEPERATOR } from "./Constants";
import { ComboBoxContext } from "./ComboBoxContext";
import { DropdownVirtualList } from "./DropdownVirtualList";
import { findText, useDataSource } from "./hooks/useDataSource";
import { useListItemHighlighter } from "./hooks/useListItemHighlighter";
import { renderItemFn } from "./Constants";
import cls from "./combo-box.module.scss";
export const DropdownList = React.forwardRef((props, forwardRef) => {
    const { value, textField, valueField, dataSource: unfilteredDataSource2, renderItem = renderItemFn, onChange, dropdownHeight = 250, dropdownWidth, minDropdownWidth, dropdownItemHeight = 30, className, disableSearch, readOnly, disabled, style, dropdownClassName, dropdownStyle, itemClassName: dropdownItemClass, itemStyle: dropdownItemStyle, selectedItemClassName: dropdownSelectedItemClass, selectedItemStyle: dropdownSelectedItemStyle, highlightedItemClassName: dropdownHighlightedItemClass, highlightedItemStyle: dropdownHighlightedItemStyle, addNewClassName: addNewClass, addNewStyle, seperatorClassName: seperatorClass, seperatorStyle, noRecordClassName: noRecordClass, noRecordStyle, onKeyDown, onClose, filter2, filter, onAddNew, searchFn, addNewElement, placeholder, hideClearButton, __keepOpen } = props;
    const [visible, setVisible] = React.useState(false);
    const [searchText, setSearchText] = React.useState("");
    const [controlRef, setControlRef] = React.useState(null);
    const focusCatcherRef = React.useRef();
    const mergeRef = useMergedRef(focusCatcherRef, forwardRef);
    const doAddNew = !!onAddNew;
    const unfilteredDataSource = "items" in unfilteredDataSource2 ? unfilteredDataSource2.items : unfilteredDataSource2;
    const { flatDataSource, getValueByIndex } = useDataSource({
        dataSource: unfilteredDataSource, filter, filter2, visible, textField,
        searchText, searchFn, doAddNew, valueField, disabled, readOnly
    });
    const selectedText = React.useMemo(() => {
        const item = findText({ dataSource: flatDataSource, valueField, value });
        if (!item)
            return undefined;
        const text = typeof textField === "function" ? textField(item) : item[textField];
        return text;
    }, [flatDataSource, textField, value, valueField]);
    const selectedIndex = React.useMemo(() => {
        return flatDataSource.findIndex(item => item === SEPERATOR || item === NO_RECORD || item === ADD_NEW ? false : item[valueField] === value);
    }, [flatDataSource, value, valueField]);
    const setVisibleFalse = React.useCallback(() => {
        setSearchText("");
        setVisible(false);
        // focusCatcherRef.current?.focus();
    }, []);
    const setSelectedIndex = React.useCallback((index) => {
        const selectedValue = getValueByIndex(index);
        setVisibleFalse();
        if (selectedValue === NULL_VALUE)
            return;
        onChange?.(selectedValue);
    }, [getValueByIndex, onChange, setVisibleFalse]);
    const { setHighlightedIndexByKeyboard, getHighlightedindex, handleKeydown, ...rest } = useListItemHighlighter(flatDataSource, setSelectedIndex);
    const setVisibleTrue = React.useCallback(() => {
        if (disabled)
            return;
        setHighlightedIndexByKeyboard(selectedIndex);
        setVisible(true);
    }, [disabled, setHighlightedIndexByKeyboard, selectedIndex, setVisible]);
    React.useEffect(() => {
        setHighlightedIndexByKeyboard(0);
    }, [setHighlightedIndexByKeyboard, searchText]);
    const onSearchInputBlur = (e) => {
        if (__keepOpen)
            return;
        setVisibleFalse();
    };
    const onSearchInputKeyDown = (e) => {
        if (visible === false) {
            if (e.key === "ArrowDown" || e.currentTarget.value) {
                setVisibleTrue();
            }
            onKeyDown?.(e);
            return;
        }
        if (!handleKeydown(e)) {
            if (e.key === "Escape") {
                if (visible === true)
                    setVisibleFalse();
            }
        }
        onKeyDown?.(e);
    };
    const onAddNewHandler = () => {
        if (!onAddNew)
            return;
        const setSelectedValue = (selectedValue) => {
            if (disabled || readOnly)
                return;
            setVisibleFalse();
            onChange?.(selectedValue);
        };
        const retValue = onAddNew();
        if (retValue === undefined) {
            setSelectedValue(undefined);
        }
        else if (retValue instanceof Promise) {
            void retValue.then(x => setSelectedValue(x));
        }
        else {
            setSelectedValue(retValue);
        }
    };
    const onPopperMouseDown = (e) => {
        e.preventDefault();
    };
    const onControlMouseDown = (e) => {
        if (e.button !== 0)
            return;
        if (e.defaultPrevented)
            return;
        const input = focusCatcherRef.current;
        if (e.target === input)
            return;
        input?.focus();
        e.preventDefault();
        const clear = controlRef?.querySelector("[data-id='clear']");
        if (clear?.contains(e.target))
            return;
        visible ? setVisibleFalse() : setVisibleTrue();
    };
    const onClear = (e) => {
        if (e.defaultPrevented)
            return;
        if (disabled || readOnly)
            return;
        setVisibleFalse();
        onChange?.(undefined);
    };
    const onSearchInputChange = (e) => {
        if (visible === false) {
            setVisibleTrue();
        }
        setSearchText(e.target.value);
    };
    const isItemSelected = (index) => selectedIndex === index;
    return _jsxs(ComboBoxContext.Provider, { value: {
            getHighlightedindex, setSelectedIndex, isItemSelected,
            dropdownItemClass, dropdownItemStyle,
            dropdownSelectedItemClass, dropdownSelectedItemStyle,
            dropdownHighlightedItemClass, dropdownHighlightedItemStyle,
            addNewClass, addNewStyle, seperatorClass, seperatorStyle,
            noRecordClass, noRecordStyle, ...rest
        }, children: [_jsx("span", { ref: setControlRef, style: style, className: classNames(className, { disabled }), onMouseDown: onControlMouseDown, children: _jsxs("div", { className: cls.controlWrapper, children: [_jsxs("div", { className: cls.selectionArea, children: [!searchText ? _jsx("div", { className: cls.search, children: selectedText }) : null, _jsx(AutoExpandInput, { ref: mergeRef, minWidth: 25, disabled: disabled, readOnly: disableSearch, className: classNames("select-inner-input", cls.input), onBlur: onSearchInputBlur, onKeyDown: onSearchInputKeyDown, onChange: onSearchInputChange, placeholder: selectedText ? undefined : placeholder, autoComplete: "off", "aria-autocomplete": "none", value: searchText })] }), hideClearButton ? null : _jsx(Clear, { size: ".75em", className: cls["mr-2"], onMouseDown: e => onClear(e) }), _jsx(Caret, { size: ".75em", flip: visible })] }) }), _jsx(PopOver, { referenceElement: controlRef, visible: visible, width: dropdownWidth, minWidth: minDropdownWidth, onClose: onClose, purpose: "select", children: _jsx("div", { onMouseDown: onPopperMouseDown, className: classNames(dropdownClassName ?? cls.listWrapper, cls.multi_listWrapper), style: { ...dropdownStyle, maxHeight: dropdownHeight }, children: _jsx(DropdownVirtualList, { flatDataSource: flatDataSource, textField: textField, valueField: valueField, renderItem: renderItem, itemHeight: dropdownItemHeight, onAddNew: onAddNewHandler, addNewElement: addNewElement }) }) })] });
});
